import { toast } from '@/components/toast';
import UploadSquare from '@/images/UploadSquare.svg';
import { getVideoMetadata } from '@/utils/utils';
import styled from '@emotion/styled';
import { FileRejection, useDropzone } from 'react-dropzone';

const UploadBoxContainer = styled('div', { shouldForwardProp: prop => prop !== 'isDragActive' })<{ isDragActive?: boolean }>`
    align-items: center;
    border-radius: 12px;

    ${({ isDragActive, theme }) =>
        isDragActive
            ? `border: 1px solid ${theme.colors.primary.main};`
            : `background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='12' ry='12' stroke='%23333' stroke-width='1' stroke-dasharray='6%2c 10' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");`}

    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 12px;
    width: 100%;
    height: 346px;
    overflow: hidden;
    padding: 1px 0;
`;

const Badge = styled.div`
    font-size: 16px;
    color: ${({ theme }) => theme.colors.primary.main};
    background-color: ${({ theme }) => theme.colors.primary.bgLight};
    border-radius: 8px;
    font-weight: 700;
    text-transform: capitalize;
    padding: 6px 8px;
`;

const Dropzone = styled(UploadBoxContainer)<{ disabled?: boolean; isDragActive?: boolean }>`
    background: ${({ isDragActive, theme }) => (isDragActive ? theme.colors.primary.bgLight : 'none')};
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    outline: none !important;
    transition: background linear 0.3s;
    border: none;
    width: calc(100% - 2px);

    &:hover {
        background: ${({ isDragActive, theme }) => (isDragActive ? theme.colors.primary.bgLight : theme.colors.black[100])};
    }

    ${({ disabled }) => disabled && 'opacity: 0.5; pointer-events: none;'}
`;

const Image = styled.img`
    width: 116px;
    height: 116px;
    margin-block-end: 4px;
`;

const UploadBoxMainText = styled.div`
    color: ${({ theme }) => theme.colors.secondary[900]};
    font-size: 24px;
    font-weight: 700;
    letter-spacing: -0.48px;
`;

const UploadBoxSubText = styled.div`
    color: ${({ theme }) => theme.colors.black[700]};
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
`;

const DragText = styled.span`
    color: ${({ theme }) => theme.colors.secondary[900]};
    font-size: 28px;
    font-weight: 700;
    letter-spacing: -0.56px;
`;

interface Props {
    onFileChange: (file: File | null) => void;
    isDurationCheckRequired?: boolean;
    minFileDurationInSeconds: number;
    maxFileDurationInSeconds: number;
    maxFileSizeInMB: number;
    uploadFootageSubtext: string;
    badgeText?: string;
    minResoultion?: number;
}

const MegaPixel = 1000000;

export const UploadFootage = ({
    onFileChange,
    isDurationCheckRequired = true,
    minFileDurationInSeconds,
    maxFileDurationInSeconds,
    maxFileSizeInMB,
    minResoultion,
    uploadFootageSubtext,
    badgeText,
}: Props) => {
    const handleDrop = async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
        if (acceptedFiles.length) {
            const validPathRegex = /^[a-zA-Z0-9_\-\/\.\s]+$/;
            const file = acceptedFiles[0];

            const { duration, videoHeight, videoWidth } = await getVideoMetadata(file);
            if (isDurationCheckRequired && duration > maxFileDurationInSeconds) {
                return toast.error({
                    title: `Videos must be shorter than ${Math.round(maxFileDurationInSeconds / 60)} minutes`,
                    toastId: 'footage-upload-error-too-long',
                });
            }

            if (isDurationCheckRequired && duration < minFileDurationInSeconds) {
                if (minFileDurationInSeconds < 60) {
                    return toast.info({
                        title: `Videos must be longer than ${minFileDurationInSeconds} seconds`,
                        toastId: 'footage-upload-error-too-short',
                    });
                }
                return toast.info({
                    title: `Videos must be longer than ${Math.round(minFileDurationInSeconds / 60)} minutes`,
                    toastId: 'footage-upload-error-too-short',
                });
            }

            if (minResoultion && (videoHeight * videoWidth) / MegaPixel < minResoultion) {
                return toast.error({
                    title: `Videos must have a higher resolution than ${minResoultion} MP`,
                    toastId: 'footage-upload-error-too-short',
                });
            }

            if (!validPathRegex.test(file.name)) {
                return toast.error({
                    title: 'Invalid file name',
                    message: 'Allowed characters: english letters, numbers, spaces, hyphens, underscores, and periods',
                    toastId: 'footage-upload-error-invalid-name',
                });
            }

            onFileChange(file || null);
        }

        if (rejectedFiles.length) {
            if (rejectedFiles[0].errors[0].code === 'file-too-large') {
                toast.error({
                    title: `Videos must be smaller than ${maxFileSizeInMB / 1024}GB`,
                    toastId: 'footage-upload-error-too-large',
                });
            }
            if (rejectedFiles[0].errors[0].code === 'file-invalid-type') {
                toast.error({ title: 'Invalid file type', toastId: 'footage-upload-error-invalid-type' });
            }
        }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: handleDrop,
        accept: { 'video/*': ['.mp4', '.webm'] },
        multiple: false,
        maxSize: 1024 * 1024 * maxFileSizeInMB,
    });

    return (
        <UploadBoxContainer isDragActive={isDragActive}>
            <Dropzone {...getRootProps()} isDragActive={isDragActive}>
                <input {...getInputProps()} data-testid="avatar-footage-file-input" />
                {isDragActive ? (
                    <DragText>Drop to upload</DragText>
                ) : (
                    <>
                        <Image src={UploadSquare} />
                        {badgeText && <Badge>{badgeText}</Badge>}
                        <UploadBoxMainText>Upload footage</UploadBoxMainText>
                        <UploadBoxSubText>{uploadFootageSubtext}</UploadBoxSubText>
                    </>
                )}
            </Dropzone>
        </UploadBoxContainer>
    );
};
